import React, { useState } from 'react';
import Papa from 'papaparse';
import { Download, Filter, AlertCircle, Loader } from 'lucide-react';
import "../styles/FilterAndDownload.css";

const FilterAndDownload = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState('');
  
  const fetchData = async (collection) => {
    setLoading(true);
    setError(null);
    setSelectedCollection(collection);

    try {
      const response = await fetch(`https://gnrl-bknd-server.onrender.com/api/data?collection=${collection}`);
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      const textResponse = await response.text();
      
      if (!textResponse) {
        throw new Error("No data received");
      }
  
      const results = Papa.parse(textResponse, { header: true });
      if (results.errors.length > 0) {
        throw new Error("Error parsing CSV data");
      }
      
      setData(results.data);
    } catch (error) {
      setError(error.message || 'Error fetching data');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    try {
      if (!data.length) {
        throw new Error('No data available to download');
      }

      const csv = Papa.unparse(data);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${selectedCollection}_data.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setError(error.message || 'Error generating CSV');
    }
  };

  return (
    <div className="filter-container" role="main">
      <h2 className="filter-title">Data Export Tool</h2>

      {error && (
        <div className="error-message" role="alert">
          <AlertCircle className="error-icon" />
          <span>{error}</span>
        </div>
      )}

      <div className="filter-content">
        <div className="select-wrapper">
          <label htmlFor="collection-select" className="select-label">
            <Filter className="select-icon" />
            Select Collection
          </label>
          <select 
            id="collection-select"
            className="collection-select" 
            onChange={(e) => fetchData(e.target.value)}
            value={selectedCollection}
            disabled={loading}
          >
            <option value="">Choose a collection</option>
            <option value="users">Users</option>
            <option value="Questions">Questions</option>
            <option value="Results">Results</option>
          </select>
        </div>
        
        <button 
          className={`download-button ${data.length === 0 ? 'disabled' : ''}`}
          onClick={downloadCSV}
          disabled={data.length === 0 || loading}
          aria-label="Download CSV file"
        >
          {loading ? (
            <>
              <Loader className="button-icon spinning" />
              Loading...
            </>
          ) : (
            <>
              <Download className="button-icon" />
              Download CSV
            </>
          )}
        </button>
      </div>

      {loading && (
        <div className="loading-indicator" role="status">
          <Loader className="loading-spinner" />
          <span>Fetching data...</span>
        </div>
      )}

      {data.length > 0 && (
        <div className="data-preview">
          <h3>Data Preview</h3>
          <div className="preview-table-container">
            <table className="preview-table">
              <thead>
                <tr>
                  {Object.keys(data[0]).map(header => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.slice(0, 5).map((row, index) => (
                  <tr key={index}>
                    {Object.values(row).map((cell, cellIndex) => (
                      <td key={cellIndex}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {data.length > 5 && (
              <div className="preview-note">
                Showing first 5 of {data.length} records
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterAndDownload;
